// src/pages/HomePageTest.jsx
import React, { useEffect, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";

// AuthKit (Azure AD)
import {
  useAuth,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "../../azure_auth/AuthKit";

// Redux (connect API)
import { connect } from "react-redux";
import {
  getUserThunk,
  addUserThunk,
} from "../../redux/users/users.thunk";

// UI & assets
import {
  Button,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import SurveySlackIndex from "../survey/survey-index/survey-slack-index";
import employee from "../../assets/employee.gif";
import ErrorBoundary from "../../azure_auth/ErrorBoundary";
import LoginWidget from "../../LoginWidget";
import { getBatabotAccessThunk } from "../../redux/batabot/batabot.thunk";

const HomePageTest = ({
  /* props from connect */
  reduxUser,
  getUserSlack,
  addUser,
  getBatabotAccess,
  /* i18n */
  t,
}) => {
  /* ──────────────────────────── AuthKit ─────────────────────────── */
  const {
    user,               // Azure AD profile (null until login)
    loading: authLoading,
    error:   authError,
    login,              // AuthKit.login()
  } = useAuth();

  /* ──────────────────────────── Teams SDK ────────────────────────── */
  const [teamsInfo, setTeamsInfo] = useState({
    insideTeams: false,
    host:        "browser",
  });

  useEffect(() => {
    (async () => {
      try {
        await microsoftTeams.app.initialize();
        const ctx = await microsoftTeams.app.getContext();
        setTeamsInfo({
          insideTeams: true,
          host: ctx.app?.host?.clientType || "teams",
        });
      } catch {
        setTeamsInfo({ insideTeams: false, host: "browser" });
      }
    })();
  }, []);

  /* ──────────────────────────── Reloj (opcional) ─────────────────── */
  const [timeInfo, setTimeInfo] = useState({
    currentTime: new Date(),
    timeZone:    Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  useEffect(() => {
    const timer = setInterval(
      () => setTimeInfo((p) => ({ ...p, currentTime: new Date() })),
      1000,
    );
    return () => clearInterval(timer);
  }, []);

  /* ─────────── Sync AuthKit → Backend + Batabot access ───────────── */
  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        // 1 ‑ Cargar lista de acceso Batabot
        await getBatabotAccess();

        // 2 ‑ Sincronizar usuario con tu backend
        const backendUser = await getUserSlack(user.username);

        if (!backendUser?.id) {
          const [first = "", ...rest] = (user.name || "").split(" ");
          await addUser({
            firstName: first,
            lastName:  rest.join(" "),
            email:     user.username,
            id:        user.id,
            tenantId:  user.tenantId || "",
          });
        }
      } catch (err) {
        console.error("Error sincronizando usuario / Batabot:", err);
      }
    })();
  }, [user, getUserSlack, addUser, getBatabotAccess]);

  /* ──────────────────────────── Render UI ────────────────────────── */

  // 1) AuthKit aún cargando
  if (authLoading) {
    return (
      <Box textAlign="center" mt={6}>
        <Typography>Cargando autenticación…</Typography>
        <CircularProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <div className="row h-100">
      {/* Ejemplo de reloj (descomenta si lo necesitas) */}
      {/* <div className="w-100 text-end p-2">
        <Typography variant="subtitle2">
          {timeInfo.currentTime.toLocaleString()} ({timeInfo.timeZone})
        </Typography>
      </div> */}

      {/* =========== USUARIO AUTENTICADO =========== */}
      <ErrorBoundary>
        <AuthenticatedTemplate>
          {reduxUser?.id ? (
            <SurveySlackIndex />
          ) : (
            <Box textAlign="center" mt={6}>
              <Typography>Cargando datos de usuario…</Typography>
              <CircularProgress sx={{ mt: 2 }} />
            </Box>
          )}
        </AuthenticatedTemplate>
      </ErrorBoundary>

      {/* ========== USUARIO NO AUTENTICADO ========== */}
      <ErrorBoundary>
        <UnauthenticatedTemplate>
            <div className="row h-100">
              <div className="col-md-6">
              <Typography
                variant="h3"           // usa h4 para tamaño ~34px
                component="h3"         // pero renderiza un <h2>
                gutterBottom
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.5   // aquí defines el interlineado
                }}
              >
                {t("home.title") ?? "Welcome to Moodbit"}
              </Typography>
                
                {/* <Typography 
                  variant="h2"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 700 }}
                >
                  {t ? t("home.title") : "Welcome to Moodbit"}
                </Typography> */}
                  <Typography
                    variant="body2"        /* arrancamos con body2 */
                    sx={{ 
                      fontSize: "1.25rem",  /* equivalente a fs‑5 de Bootstrap */ 
                      mb: 3 
                    }}
                  >
                  {t ? t("home.description") : "Sign in to access your surveys."}
                </Typography>
                {authError && (
                  <Typography color="error" sx={{ mb: 2 }}>
                    {authError.message}
                  </Typography>
                )}
            <Button
              variant="outlined"
              size="large"
              onClick={login}
              sx={{ fontSize: "1.125rem" }}
            >
              Sign in
            </Button>


              </div>
              <div className="col-md-6 text-center">
                <img src={employee} alt="employee" width="100%" />
              </div>
            </div>
        </UnauthenticatedTemplate>
      </ErrorBoundary>
    </div>
  );
};

/* ─────────────────────── Redux connect() ─────────────────────────── */
const mapStateToProps = (state) => ({
  reduxUser: Array.isArray(state.users.user)
    ? state.users.user[0]
    : state.users.user,
});

const mapDispatchToProps = (dispatch) => ({
  getUserSlack:     (email)   => dispatch(getUserThunk(email)),
  addUser:          (userObj) => dispatch(addUserThunk(userObj)),
  getBatabotAccess: ()        => dispatch(getBatabotAccessThunk()),
});

export default connect(mapStateToProps, mapDispatchToProps)(HomePageTest);
